import { getSelectCountryFilter } from 'components/react-select/CountryFunctions'
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { UserContext } from 'shared/users/contexts/UserContext'
import { SecurityEnum } from 'features/declaration/models/schemas/declarationEnums'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { isEnsType } from 'features/declaration/models/helpers'
import { extractCountryFromDepartureOffice } from 'shared/ncts/common/classifier-util'
import useCodelist, { createDepartureCustomsOfficesByDeclarationTypeAndUserFilter } from 'features/declaration/codelist/api/useCodelist'
import {
  CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestSecurityEnum,
} from 'api/models'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import Input from 'components/p5/Input'
import FormSelect from 'components/p5/FormSelect'
import FormField from 'components/p5/FormField'
import {
  getAdditionalDeclarationTypeConfiguration,
  getSecurityConfiguration,
  getTransitDeclarationTypeConfiguration,
  NctsCountry,
} from 'config'
import {
  ADDITIONAL_DECLARATION_TYPE_OPTIONS,
  DECLARATION_TYPE_OPTIONS,
  SECURITY_OPTIONS,
  SPECIFIC_CIRCUMSTANCE_INDICATOR_OPTIONS,
  TRANSIT_DECLARATION_TYPE_OPTIONS,
} from 'features/declaration/models/declarationOptions'
import { ISelectOption } from 'types/IClassifier'
import { hasText } from 'shared/lib/common-util'
import { useCalculatorModal } from 'features/declaration/form/components/calculator/CalculatorModalContext'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import RadioGroup from 'components/p5/RadioGroup'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import { useDeclarationEditorContext } from 'features/declaration/form/contexts/DeclarationEditorContext'

function getSecuritiesAsOptions(supportedSecurities: CreateOrUpdateTransitOperationRequestSecurityEnum[]): ISelectOption[] {
  return supportedSecurities.map((value) => {
    const option = SECURITY_OPTIONS.find((securityOption) => securityOption.value === value)

    if (!option) {
      throw new Error(`Unsupported security value: ${value}`)
    }

    return option
  })
}

const {
  ENS,
} = CreateOrUpdateTransitOperationRequestDeclarationTypeEnum

function getAdditionalDeclarationTypeOptions(country: NctsCountry): ISelectOption[] {
  const supportedTypes: string[] = getAdditionalDeclarationTypeConfiguration(country)

  return ADDITIONAL_DECLARATION_TYPE_OPTIONS.filter((option) => supportedTypes.includes(option.value))
}

function isDeclarationTypeLocked(declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum, country: NctsCountry): boolean {
  return declarationType === ENS || getTransitDeclarationTypeConfiguration(country).length <= 1
}

function isSecurityLocked(
  declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
  mrn: string | null | undefined,
  status: string | null | undefined,
  country: NctsCountry,
): boolean {
  return isEnsType(declarationType) || (country === 'SK' && (hasText(mrn) || status === 'SUBMITTED'))
}

function GeneralSection() {
  const { t } = useTranslation()

  const { updateTransitEditorData } = useDeclarationEditorContext()
  const {
    user,
  } = useContext(UserContext)

  const {
    control, getValues, setValue, resetField,
  } = useFormContext<TransitOperationFormValues>()

  const office = getValues('departureCustomsOffice')
  const declarationType = getValues('declarationType')
  const country = useMemo(() => office.slice('DEPARTURE_OFFICE_'.length, 'DEPARTURE_OFFICE_'.length + 2), [office]) as NctsCountry
  const [securityOptions, setSecurityOptions] = useState(getSecuritiesAsOptions(getSecurityConfiguration(country)))
  const [declarationTypeOptions, setDeclarationTypeOptions] = useState([...DECLARATION_TYPE_OPTIONS])
  const [additionalDeclarationTypeOptions, setAdditionalDeclarationTypeOptions] = useState(getAdditionalDeclarationTypeOptions(country))
  const { options: countriesOptions } = useCodelistOptions('COUNTRIES')

  const [
    security,
    additionalDeclarationType,
    departureCustomsOffice,
    inlandModeOfTransport,
    borderModeOfTransport,
    countriesOfRouting,
    specificCircumstanceIndicator,
  ] = useWatch({
    control,
    name: [
      'security',
      'additionalDeclarationType',
      'departureCustomsOffice',
      'inlandModeOfTransport',
      'borderModeOfTransport',
      'countryOfRoutingOfConsignment',
      'specificCircumstanceIndicator',
    ],
  })

  useEffect(() => {
    updateTransitEditorData({
      security,
      additionalDeclarationType,
      specificCircumstanceIndicator,
      departureCustomsOffice,
      country: extractCountryFromDepartureOffice(departureCustomsOffice),
      inlandModeOfTransport,
      borderModeOfTransport,
      countriesOfRouting: countriesOfRouting?.length
        ? countriesOfRouting
          .filter((c) => c.country)
          .map((c) => c.country as string)
        : [],
    })
  }, [
    updateTransitEditorData,
    security,
    additionalDeclarationType,
    specificCircumstanceIndicator,
    departureCustomsOffice,
    inlandModeOfTransport,
    borderModeOfTransport,
    countriesOfRouting,
  ])

  const departureCustomsOfficeFilterFn = useCallback(createDepartureCustomsOfficesByDeclarationTypeAndUserFilter, [])
  const departureCustomsOfficesFilterPredicate = useMemo(
    () => departureCustomsOfficeFilterFn(declarationType, user),
    [departureCustomsOfficeFilterFn, declarationType, user],
  )
  const { options: departureCustomsOfficeOptions, isLoading: isDepartureCustomsOfficesLoading } = useCodelistOptions('DEPARTURE_CUSTOMS_OFFICES', departureCustomsOfficesFilterPredicate)

  const [, destinationCustomsOfficesOptions] = useCodelist('DESTINATION_CUSTOMS_OFFICES')
  const [, paymentMethodOptions] = useCodelist('PAYMENT_METHODS')
  // const houseConsignments = useMemo(() => getValues('houseConsignment'), [getValues('houseConsignment')])

  // const minimumConsignmentGrossMass = R0994Validator.getMinimumConsignmentGrossMass(houseConsignments)
  const minimumConsignmentGrossMass = 0

  const { open } = useCalculatorModal()

  useEffect(() => {
    const newSecurityOptions = getSecuritiesAsOptions(getSecurityConfiguration(country))
    setSecurityOptions(newSecurityOptions)
    if (newSecurityOptions.findIndex((option) => option.value === security) === -1) {
      setValue('security', SecurityEnum.Enum['0'])
    }
  }, [country, security, setValue])

  useEffect(() => {
    const newAdditionalDeclarationTypeOptions = getAdditionalDeclarationTypeOptions(country)
    setAdditionalDeclarationTypeOptions(newAdditionalDeclarationTypeOptions)
    if (!newAdditionalDeclarationTypeOptions.some((option) => option.value === additionalDeclarationType)) {
      setValue('additionalDeclarationType', CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum.A)
    }
  }, [country, additionalDeclarationType, setValue])

  useEffect(() => {
    if (isEnsType(getValues('declarationType'))) {
      setValue('security', SecurityEnum.Enum['0'])
      setDeclarationTypeOptions([...DECLARATION_TYPE_OPTIONS])
      return
    }

    if (country) {
      const supportedTypes = getTransitDeclarationTypeConfiguration(country)
      setDeclarationTypeOptions(DECLARATION_TYPE_OPTIONS.filter((option) => supportedTypes.includes(option.value)))

      if (!supportedTypes.includes(declarationType)) {
        setValue('declarationType', supportedTypes[0])
      }
    } else {
      setDeclarationTypeOptions([...TRANSIT_DECLARATION_TYPE_OPTIONS])
    }
  }, [declarationType, country, getValues, setValue])

  useEffect(() => {
    if (isDepartureCustomsOfficesLoading) return
    if (!departureCustomsOfficeOptions.some((option) => option.value === departureCustomsOffice)) {
      resetField('departureCustomsOffice', { defaultValue: '' })
    }
  }, [departureCustomsOffice, departureCustomsOfficeOptions, isDepartureCustomsOfficesLoading, resetField])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', [
    'additionalDeclarationType',
    'entryIndicator',
    'countryOfDestination',
    'countryOfDispatch',
    'specificCircumstanceIndicator',
    'departureCustomsOffice',
    'destinationCustomsOffice',
    'grossMass',
    'limitDate',
    'referenceNumberUCR',
    'security',
    'transportChargesMethodOfPayment',
  ])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-general"
        headingLabel={t('declaration.navbar.general')}
      >

        <FormField labelKey="declaration.p5.mrn">
          <Input field="mrn" labelKey="declaration.p5.mrn" type="text" placeholder={t('declaration.p5.mrn')} disabled />
        </FormField>

        <FormField labelKey="declaration.p5.lrn">
          <Input field="lrn" labelKey="declaration.p5.lrn" type="text" placeholder={t('declaration.p5.lrn')} disabled />
        </FormField>

        <FormField
          labelKey="declaration.p5.declarationType"
          fieldRule={fieldRequirements.declarationType}
        >
          <FormSelect
            field="declarationType"
            labelKey="declaration.p5.declarationType"
            type="sync"
            isDisabled={isDeclarationTypeLocked(getValues('declarationType'), country)}
            options={declarationTypeOptions}
            fieldRule={fieldRequirements.declarationType}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.additionalDeclarationType"
          fieldRule={fieldRequirements.additionalDeclarationType}
        >
          <FormSelect
            field="additionalDeclarationType"
            labelKey="declaration.p5.additionalDeclarationType"
            options={additionalDeclarationTypeOptions}
            type="sync"
            fieldRule={fieldRequirements.additionalDeclarationType}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.security"
          fieldRule={fieldRequirements.security}
        >
          <FormSelect
            field="security"
            labelKey="declaration.p5.security"
            isDisabled={isSecurityLocked(getValues('declarationType'), getValues('mrn'), getValues('status'), country)}
            options={securityOptions}
            type="sync"
            fieldRule={fieldRequirements.security}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.specificCircumstanceIndicator"
          fieldRule={fieldRequirements.specificCircumstanceIndicator}
        >
          <FormSelect
            field="specificCircumstanceIndicator"
            labelKey="declaration.p5.specificCircumstanceIndicator"
            isDisabled={isEnsType(getValues('declarationType'))}
            options={SPECIFIC_CIRCUMSTANCE_INDICATOR_OPTIONS}
            type="sync"
            fieldRule={fieldRequirements.specificCircumstanceIndicator}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.entryIndicator"
          fieldRule={fieldRequirements.entryIndicator}
        >
          <RadioGroup
            <TransitOperationFormValues>
            field="entryIndicator"
            labelKey="common.no_entryIndicator"
            type="radio"
            radioOptions={[
              { label: 'common.no_entryIndicator', value: 'false' },
              { label: 'common.yes_entryIndicator', value: 'true' },
            ]}
            fieldRule={fieldRequirements.entryIndicator}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.dispatchCountry"
          fieldRule={fieldRequirements.countryOfDispatch}
        >
          <FormSelect
            <TransitOperationFormValues>
            field="countryOfDispatch"
            labelKey="declaration.p5.dispatchCountry"
            options={countriesOptions}
            customFilterOption={getSelectCountryFilter}
            type="sync"
            fieldRule={fieldRequirements.countryOfDispatch}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.departureCustomsOffice"
          fieldRule={fieldRequirements.departureCustomsOffice}
        >
          <FormSelect
            field="departureCustomsOffice"
            labelKey="declaration.p5.departureCustomsOffice"
            options={departureCustomsOfficeOptions}
            type="sync"
            fieldRule={fieldRequirements.departureCustomsOffice}
          />
        </FormField>

        <FormField
          labelKey="declaration.p5.destinationCountry"
          fieldRule={fieldRequirements.countryOfDestination}
        >
          <FormSelect
            field="countryOfDestination"
            labelKey="declaration.p5.destinationCountry"
            options={countriesOptions}
            customFilterOption={getSelectCountryFilter}
            type="sync"
            fieldRule={fieldRequirements.countryOfDestination}
          />
        </FormField>

        <FormField
          labelKey="declaration.p5.destinationCustomsOffice"
          fieldRule={fieldRequirements.destinationCustomsOffice}
        >
          <FormSelect
            field="destinationCustomsOffice"
            labelKey="declaration.p5.destinationCustomsOffice"
            options={destinationCustomsOfficesOptions}
            type="sync"
            fieldRule={fieldRequirements.destinationCustomsOffice}
          />
        </FormField>

        <FormField
          labelKey="declaration.p5.grossWeight"
          fieldRule={fieldRequirements.grossMass}
        >
          <div className="input-group">
            <Input
              field="grossMass"
              labelKey="declaration.p5.grossWeight"
              type="number"
              min={minimumConsignmentGrossMass}
              inputMode="decimal"
              step={0.001}
              placeholder={t('declaration.p5.grossWeight')}
              fieldRule={fieldRequirements.grossMass}
            />
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-light h-100 border fw-bold"
                onClick={() => {
                  open({
                    onResolve: (value: number) => {
                      setValue('grossMass', value)
                    },
                  })
                }}
              >
                <i className="fas fa-calculator" />
              </button>
            </div>
          </div>
        </FormField>
        <FormField
          labelKey="declaration.p5.referenceNumberUCR"
          fieldRule={fieldRequirements.referenceNumberUCR}
        >
          <Input
            field="referenceNumberUCR"
            type="text"
            labelKey="declaration.p5.referenceNumberUCR"
            placeholder={t('declaration.p5.referenceNumberUCR')}
            fieldRule={fieldRequirements.referenceNumberUCR}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.transportChargesMethodOfPayment"
          fieldRule={fieldRequirements.transportChargesMethodOfPayment}
        >
          <FormSelect
            field="transportChargesMethodOfPayment"
            labelKey="declaration.p5.transportChargesMethodOfPayment"
            options={paymentMethodOptions}
            type="sync"
            fieldRule={fieldRequirements.transportChargesMethodOfPayment}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.limitDate"
          fieldRule={fieldRequirements.limitDate}
        >
          <Input
            field="limitDate"
            type="date"
            labelKey="declaration.p5.limitDate"
            placeholder={t('declaration.p5.limitDate')}
            fieldRule={fieldRequirements.limitDate}
          />
        </FormField>
      </FormCollapsibleColumn>
    </section>
  )
}

export default GeneralSection
